import { Car } from '../../lib/supabase';
import { EyeOff, Star, GripVertical } from 'lucide-react';
import { formatDistance } from '../../lib/conversions';
import { usePreferences } from '../../contexts/PreferencesContext';
import { CachedImage } from '../ui/CachedImage';
import { theme } from '../../lib/theme';
import { useTranslations } from '../../i18n';

type CompactCarCardProps = {
  car: Car & { 
    totalExpenses: number; 
    expenseCount?: number;
    latestMileage?: number | null; 
    estimatedMileage?: number | null;
    overallResult?: number | null;
    nextReminder?: { name: string; date: string; description?: string | null; action_required: boolean }[] | null;
  };
  gradient: string;
  image?: string;
  onClick: () => void;
  dragHandleProps?: any;
  preferencesOverride?: any;
  hideFavoriteIcon?: boolean;
  starOnRight?: boolean;
};

export function CompactCarCard({ car, gradient, image, onClick, dragHandleProps, preferencesOverride, hideFavoriteIcon, starOnRight }: CompactCarCardProps) {
  const { preferences: globalPreferences } = usePreferences();
  const { t } = useTranslations();
  const effectivePreferences = preferencesOverride || globalPreferences;
  const textColor = `hsl(${car.icon_text_color_hue || 0}, 70%, ${car.icon_text_color_hue === 0 ? '100%' : car.icon_text_color_hue === 360 ? '20%' : '50%'})`;

  return (
    <div className={`flex items-center group bg-transparent hover:bg-gray-50 dark:hover:bg-white/5 transition-all border-b ${theme.colors.border} last:border-b-0`}>
      {dragHandleProps && (
        <div {...dragHandleProps} className="pl-4 py-8 text-gray-300 dark:text-gray-600 hover:text-gray-400 dark:hover:text-gray-500 cursor-grab active:cursor-grabbing">
          <GripVertical size={20} />
        </div>
      )}
      <button
        onClick={onClick}
        className={`flex-1 text-left flex items-center gap-5 p-5 active:scale-[0.99] font-inter`}
      >
        <div className="flex-1 flex items-center gap-4">
          {image && car.use_image_icon ? (
            <div className="w-28 h-20 rounded-2xl overflow-hidden flex-shrink-0 bg-gray-100 shadow-inner border border-gray-100">
              <CachedImage src={image} alt={car.name || car.make} className="w-full h-full object-cover" />
            </div>
          ) : (
            <div
              className={`rounded-2xl w-28 h-20 flex items-center justify-center flex-shrink-0 shadow-inner`}
              style={{ background: gradient }}
            >
              <span
                className="text-5xl font-black whitespace-pre-wrap text-center leading-tight px-2 select-none"
                style={{ color: textColor }}
              >
                {car.icon_text || (car.name?.[0] || car.make?.[0] || 'C').toUpperCase()}
              </span>
            </div>
          )}

          <div className="flex-1 min-w-0 py-1">
            <div className="flex items-center justify-between gap-2 mb-0.5">
              <div className="flex items-center gap-2">
                <h3 className={theme.typography.itemTitle}>
                  {car.name || `${car.make} ${car.model}`}
                </h3>
                {car.is_favorite && !hideFavoriteIcon && !starOnRight && <Star size={16} className="text-yellow-500 flex-shrink-0" fill="currentColor" />}
              </div>
              {starOnRight && (
                ((car.favorite_count ?? 0) > 0) ? (
                  <div className="flex items-center gap-1.5 bg-yellow-50 dark:bg-yellow-900/20 px-2 py-1 rounded-xl border border-yellow-100 dark:border-yellow-900/40 shadow-sm">
                    {car.is_public && car.favorite_count !== undefined && car.favorite_count > 0 && (
                      <span className="text-xs font-black text-yellow-600 dark:text-yellow-500">
                        {car.favorite_count}
                      </span>
                    )}
                    <Star size={16} className={car.is_favorite ? "text-yellow-500 fill-yellow-500" : "text-yellow-500"} />
                  </div>
                ) : (!car.is_public && !car.is_archived && (
                  <div className="flex items-center bg-red-50 dark:bg-red-900/20 px-2 py-1 rounded-xl border border-red-100 dark:border-red-900/40 shadow-sm">
                    <EyeOff size={16} className="text-red-500/80" />
                  </div>
                ))
              )}
            </div>

            {car.name && (
              <p className={`${theme.typography.itemSubtitle} mb-0.5 font-bold text-gray-700 dark:text-gray-300`}>
                {car.make} {car.model}
              </p>
            )}

            <div className={`flex flex-wrap items-center gap-x-2 gap-y-0.5 ${theme.typography.itemSubtitle} uppercase tracking-wider`}>
              <span>{car.year}</span>
              {car.is_archived && car.bought_at && (
                <>
                  <span className="text-gray-200 dark:text-gray-700">·</span>
                  <span className="normal-case">
                    {(() => {
                      const d1 = new Date(car.bought_at);
                      const d2 = car.sell_date ? new Date(car.sell_date) : null;
                      const months = t.common.monthsShort as string[];
                      const fmt = (d: Date) => `${d.getFullYear()} ${months[d.getMonth()]}`;
                      return d2 ? `${fmt(d1)} - ${fmt(d2)}` : fmt(d1);
                    })()}
                  </span>
                </>
              )}
              {car.fuel_type && !car.is_archived && (
                <>
                  <span className="text-gray-200 dark:text-gray-700">·</span>
                  <span className="normal-case">{(t.fuelTypes as any)[car.fuel_type] || car.fuel_type}</span>
                </>
              )}
              {(car.estimatedMileage || car.latestMileage) && effectivePreferences && !car.is_archived && (
                <>
                  <span className="text-gray-200 dark:text-gray-700">·</span>
                  <span className="font-bold">
                    ~ {formatDistance(car.estimatedMileage || car.latestMileage || 0, car.distance_unit || effectivePreferences.distance_unit)}
                  </span>
                </>
              )}
              {car.nextReminder && car.nextReminder.length > 0 && !car.is_archived && (
                <div className="w-full mt-2 flex flex-col gap-2">
                  {car.nextReminder.map((reminder, idx) => (
                    <div key={`${car.id}-rem-${idx}`}>
                      <span className={`inline-flex items-center gap-2 text-[11px] font-black ${reminder.action_required ? 'text-green-600 dark:text-green-400' : 'text-blue-600 dark:text-blue-400'} normal-case ${reminder.action_required ? 'bg-green-50 dark:bg-green-900/20' : 'bg-blue-50 dark:bg-blue-900/20'} px-2 py-1 rounded-xl border ${reminder.action_required ? 'border-green-200 dark:border-green-900/40 animate-remind-pulse' : 'border-blue-200 dark:border-blue-900/40'} shadow-sm`}>
                        <span className={`w-2 h-2 rounded-full ${reminder.action_required ? 'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.6)]' : 'bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.6)]'}`} />
                        {new Date(reminder.date).toLocaleDateString(undefined, { day: '2-digit', month: 'short' })}: {reminder.name}
                        {reminder.description && (
                          <div className="text-[10px] text-gray-500 dark:text-gray-400 line-clamp-1 italic font-medium mt-0.5 ml-4">
                            {reminder.description}
                          </div>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {car.is_archived && car.overallResult !== undefined && car.overallResult !== null && (
                <div className="ml-auto">
                  <span className={`font-black ${car.overallResult >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}`}>
                    {car.overallResult > 0 ? '+' : ''}{car.overallResult.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })} {effectivePreferences?.currency_symbol || '€'}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </button>
    </div>
  );
}
